import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Input, Textarea, InputAmount, Badge } from '@/backoffice/components/ui';
import { LocalizationInputGroup, type LocalizationValue } from '@/backoffice/components/forms/LocalizationInputGroup';
import { MapLocationPicker, type MapCoordinates } from '@/backoffice/components/forms/MapLocationPicker';
import { formatCurrency } from '@/backoffice/utils';
import { listDeliveryTypes, listPaymentMethods } from '@/domains/api/delivery.api';
import { fetchOrderQuote, listPackageCategories } from '@/domains/api/messaging.api';
import { resolveSecteur } from '@/domains/api/geography.api';
import { createOrder } from '@/domains/client/services/client.service';
import type { ApiDeliveryType, ApiOrderQuote, ApiPackageCategory, ApiPaymentMethod } from '@/domains/api/types';
import { isCashMethod, isMobileMoneyMethod } from '@/domains/client/utils/paymentMethod';
import {
  Package,
  Truck,
  ChevronRight,
  ChevronLeft,
  Check,
  Info,
  AlertCircle,
  Loader2,
  Banknote,
  Smartphone,
} from 'lucide-react';

type Step = 'type' | 'pickup' | 'dropoff' | 'details' | 'payment';
type DriverTypeKey = 'professional' | 'community';

const DRIVER_TYPE_MAP: Record<DriverTypeKey, 'professional_delivery' | 'community_delivery'> = {
  professional: 'professional_delivery',
  community: 'community_delivery',
};

const driverTypes = [
  { id: 'professional' as DriverTypeKey, label: 'Livreur Professionnel', description: 'Coursier dédié et assuré' },
  { id: 'community' as DriverTypeKey, label: 'Livreur Communautaire', description: 'Voisin de confiance' },
];

const steps: { key: Step; label: string }[] = [
  { key: 'type', label: 'Type' },
  { key: 'pickup', label: 'Collecte' },
  { key: 'dropoff', label: 'Livraison' },
  { key: 'details', label: 'Colis' },
  { key: 'payment', label: 'Confirmation' },
];

const emptyLoc: LocalizationValue = {
  countryId: null, regionId: null, prefectureId: null, communeId: null, districtId: null, secteurId: null,
};

function toNumber(value: number | string | null | undefined): number {
  if (value == null) return 0;
  return typeof value === 'number' ? value : parseFloat(value) || 0;
}

export function NewOrder() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('type');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deliveryTypes, setDeliveryTypes] = useState<ApiDeliveryType[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<ApiPaymentMethod[]>([]);
  const [packageCategories, setPackageCategories] = useState<ApiPackageCategory[]>([]);
  const [quote, setQuote] = useState<ApiOrderQuote | null>(null);

  const [formData, setFormData] = useState({
    deliveryTypeId: null as number | null,
    driverType: '' as DriverTypeKey | '',
    packageCategoryId: null as number | null,
    pickupLoc: { ...emptyLoc },
    dropoffLoc: { ...emptyLoc },
    pickupCoords: null as MapCoordinates | null,
    dropoffCoords: null as MapCoordinates | null,
    pickupCenter: null as MapCoordinates | null,
    dropoffCenter: null as MapCoordinates | null,
    departureAddress: '',
    arrivalAddress: '',
    departureLandmark: '',
    arrivalLandmark: '',
    packageDescription: '',
    packageWeight: 1,
    packageVolume: 0,
    declaredValue: 0,
    needsInsurance: false,
    notes: '',
    payNow: false,
    paymentMethodId: null as number | null,
  });

  const selectedDeliveryType = deliveryTypes.find((t) => t.id === formData.deliveryTypeId);
  const selectedCategory = packageCategories.find((c) => c.id === formData.packageCategoryId);
  const maxWeightHint = selectedCategory?.max_weight_hint ? toNumber(selectedCategory.max_weight_hint) : null;

  const loadQuote = useCallback(async () => {
    if (!formData.deliveryTypeId || !formData.pickupLoc.secteurId || !formData.dropoffLoc.secteurId) {
      setQuote(null);
      return;
    }
    setQuoting(true);
    try {
      const result = await fetchOrderQuote({
        delivery_type_id: formData.deliveryTypeId,
        departure_secteur_id: formData.pickupLoc.secteurId,
        arrival_secteur_id: formData.dropoffLoc.secteurId,
        departure_latitude: formData.pickupCoords?.lat ?? null,
        departure_longitude: formData.pickupCoords?.lng ?? null,
        arrival_latitude: formData.dropoffCoords?.lat ?? null,
        arrival_longitude: formData.dropoffCoords?.lng ?? null,
        estimated_weight: formData.packageWeight,
        package_category_id: formData.packageCategoryId,
      });
      setQuote(result);
    } catch {
      setQuote(null);
    } finally {
      setQuoting(false);
    }
  }, [
    formData.deliveryTypeId,
    formData.pickupLoc.secteurId,
    formData.dropoffLoc.secteurId,
    formData.pickupCoords,
    formData.dropoffCoords,
    formData.packageWeight,
    formData.packageCategoryId,
  ]);

  useEffect(() => {
    Promise.all([listDeliveryTypes(), listPaymentMethods(), listPackageCategories()])
      .then(([dt, pm, cats]) => {
        setDeliveryTypes(dt);
        setPaymentMethods(pm);
        setPackageCategories(cats);
        if (pm.length > 0) {
          const preferred = pm.find((m) => isMobileMoneyMethod(m.name)) ?? pm[0];
          setFormData((prev) => ({
            ...prev,
            paymentMethodId: preferred.id,
            payNow: isMobileMoneyMethod(preferred.name),
          }));
        }
        if (cats.length > 0) setFormData((prev) => ({ ...prev, packageCategoryId: cats[0].id }));
      })
      .catch(() => setError('Impossible de charger les données de commande.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (step === 'payment' || step === 'details') {
      loadQuote();
    }
  }, [step, loadQuote]);

  useEffect(() => {
    if (!formData.pickupLoc.secteurId) return;
    resolveSecteur(formData.pickupLoc.secteurId).then((h) => {
      if (h.secteur.latitude && h.secteur.longitude) {
        const center = { lat: h.secteur.latitude, lng: h.secteur.longitude };
        setFormData((p) => ({
          ...p,
          pickupCenter: center,
          pickupCoords: p.pickupCoords ?? center,
        }));
      }
    }).catch(() => undefined);
  }, [formData.pickupLoc.secteurId]);

  useEffect(() => {
    if (!formData.dropoffLoc.secteurId) return;
    resolveSecteur(formData.dropoffLoc.secteurId).then((h) => {
      if (h.secteur.latitude && h.secteur.longitude) {
        const center = { lat: h.secteur.latitude, lng: h.secteur.longitude };
        setFormData((p) => ({
          ...p,
          dropoffCenter: center,
          dropoffCoords: p.dropoffCoords ?? center,
        }));
      }
    }).catch(() => undefined);
  }, [formData.dropoffLoc.secteurId]);

  const totalPrice = (quote?.total_delivery_fee ?? 0) + (formData.needsInsurance ? 2500 : 0);
  const currentStepIndex = steps.findIndex((s) => s.key === step);

  const canProceed = () => {
    switch (step) {
      case 'type':
        return formData.deliveryTypeId && formData.driverType;
      case 'pickup':
        return formData.pickupLoc.secteurId && formData.departureAddress.trim();
      case 'dropoff':
        return formData.dropoffLoc.secteurId && formData.arrivalAddress.trim();
      case 'details':
        return formData.packageDescription.trim() && formData.packageWeight > 0
          && (!maxWeightHint || formData.packageWeight <= maxWeightHint);
      case 'payment':
        return quote != null && formData.paymentMethodId != null;
      default:
        return false;
    }
  };

  const handleNext = () => {
    const next = currentStepIndex + 1;
    if (next < steps.length) setStep(steps[next].key);
  };

  const handleSubmit = async () => {
    if (!formData.deliveryTypeId || !formData.pickupLoc.secteurId || !formData.dropoffLoc.secteurId || !formData.driverType || !quote) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await createOrder({
        delivery_type_id: formData.deliveryTypeId,
        requested_driver_type: DRIVER_TYPE_MAP[formData.driverType],
        departure_secteur_id: formData.pickupLoc.secteurId,
        arrival_secteur_id: formData.dropoffLoc.secteurId,
        departure_address: formData.departureAddress.trim(),
        arrival_address: formData.arrivalAddress.trim(),
        departure_landmark: formData.departureLandmark.trim() || null,
        arrival_landmark: formData.arrivalLandmark.trim() || null,
        departure_latitude: formData.pickupCoords?.lat ?? null,
        departure_longitude: formData.pickupCoords?.lng ?? null,
        arrival_latitude: formData.dropoffCoords?.lat ?? null,
        arrival_longitude: formData.dropoffCoords?.lng ?? null,
        package_category_id: formData.packageCategoryId,
        package_description: [formData.packageDescription.trim(), formData.notes.trim()].filter(Boolean).join('\n'),
        estimated_weight: formData.packageWeight,
        estimated_volume: formData.packageVolume > 0 ? formData.packageVolume : null,
        declared_value: formData.declaredValue,
        pay_now: formData.payNow,
        payment_method_id: formData.paymentMethodId,
        total_delivery_fee: totalPrice - (formData.needsInsurance ? 2500 : 0),
      });
      if (formData.payNow) {
        const isMobileMoney = isMobileMoneyMethod(
          paymentMethods.find((p) => p.id === formData.paymentMethodId)?.name,
        );
        if (isMobileMoney) {
          navigate(`/dashboard/client/commandes/${created.id}/paiement`);
          return;
        }
      }
      navigate('/dashboard/client/commandes');
    } catch {
      setError('Échec de la création de la commande.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement...
        </div>
      );
    }

    switch (step) {
      case 'type':
        return (
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Type de livraison</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {deliveryTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, deliveryTypeId: type.id }))}
                    className={`p-4 rounded-xl border-2 text-left ${formData.deliveryTypeId === type.id ? 'bg-teal-50 border-teal-400' : 'border-gray-200'}`}
                  >
                    <h4 className="font-medium">{type.name}</h4>
                    <div className="flex flex-wrap gap-1 mt-2">
                      <Badge variant="primary" size="sm">{toNumber(type.amount_per_kg)} GNF/kg</Badge>
                      {type.amount_per_km != null && <Badge variant="default" size="sm">{toNumber(type.amount_per_km)} GNF/km</Badge>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Type de livreur souhaité</h3>
              <p className="text-sm text-gray-500 mb-4">Un livreur disponible dans la zone prendra en charge votre commande.</p>
              <div className="grid md:grid-cols-2 gap-4">
                {driverTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, driverType: type.id }))}
                    className={`p-4 rounded-xl border-2 text-left ${formData.driverType === type.id ? 'bg-blue-50 border-blue-400' : 'border-gray-200'}`}
                  >
                    <Truck className="w-6 h-6 text-blue-600 mb-2" />
                    <h4 className="font-medium">{type.label}</h4>
                    <p className="text-sm text-gray-500">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'pickup':
        return (
          <div className="space-y-6">
            <LocalizationInputGroup value={formData.pickupLoc} onChange={(v) => setFormData((p) => ({ ...p, pickupLoc: v }))} />
            <Input label="Adresse de collecte *" value={formData.departureAddress} onChange={(e) => setFormData((p) => ({ ...p, departureAddress: e.target.value }))} />
            <Input label="Point de repère" value={formData.departureLandmark} onChange={(e) => setFormData((p) => ({ ...p, departureLandmark: e.target.value }))} />
            {formData.pickupLoc.secteurId && (
              <MapLocationPicker
                label="Affiner la position de collecte"
                value={formData.pickupCoords}
                center={formData.pickupCenter}
                onChange={(coords) => setFormData((p) => ({ ...p, pickupCoords: coords }))}
              />
            )}
          </div>
        );

      case 'dropoff':
        return (
          <div className="space-y-6">
            <LocalizationInputGroup value={formData.dropoffLoc} onChange={(v) => setFormData((p) => ({ ...p, dropoffLoc: v }))} />
            <Input label="Adresse de livraison *" value={formData.arrivalAddress} onChange={(e) => setFormData((p) => ({ ...p, arrivalAddress: e.target.value }))} />
            <Input label="Point de repère" value={formData.arrivalLandmark} onChange={(e) => setFormData((p) => ({ ...p, arrivalLandmark: e.target.value }))} />
            {formData.dropoffLoc.secteurId && (
              <MapLocationPicker
                label="Affiner la position de livraison"
                value={formData.dropoffCoords}
                center={formData.dropoffCenter}
                onChange={(coords) => setFormData((p) => ({ ...p, dropoffCoords: coords }))}
              />
            )}
          </div>
        );

      case 'details':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie de colis</label>
              <div className="grid md:grid-cols-2 gap-2">
                {packageCategories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, packageCategoryId: cat.id }))}
                    className={`p-3 rounded-lg border text-left text-sm ${formData.packageCategoryId === cat.id ? 'border-teal-400 bg-teal-50' : 'border-gray-200'}`}
                  >
                    <p className="font-medium">{cat.name}</p>
                    <p className="text-gray-500 text-xs">{cat.description}</p>
                    {cat.max_weight_hint && <p className="text-xs text-teal-700 mt-1">Max {cat.max_weight_hint} kg</p>}
                  </button>
                ))}
              </div>
            </div>
            <Textarea label="Description du colis *" value={formData.packageDescription} onChange={(e) => setFormData((p) => ({ ...p, packageDescription: e.target.value }))} />
            <Input
              label="Poids (kg) *"
              type="number"
              min={0}
              step="0.1"
              value={formData.packageWeight}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  packageWeight: Number(e.target.value),
                }))
              }
            />

            {maxWeightHint && Number(formData.packageWeight) > maxWeightHint && (
              <p className="text-sm text-red-600">
                Poids max pour cette catégorie : {maxWeightHint} kg
              </p>
            )}
            <InputAmount label="Valeur déclarée (GNF)" value={formData.declaredValue} onChange={(v) => setFormData((p) => ({ ...p, declaredValue: v }))} />
            <Textarea label="Notes" value={formData.notes} onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))} />
          </div>
        );

      case 'payment':
        return (
          <div className="space-y-6">
            <Card className="bg-teal-50 border-teal-200">
              <p className="text-sm text-teal-800 flex items-start gap-2">
                <Info className="w-5 h-5 shrink-0 mt-0.5" />
                Votre commande sera visible par les livreurs disponibles dans les secteurs sélectionnés.
              </p>
            </Card>
            {quoting ? (
              <p className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Calcul du tarif…</p>
            ) : quote && (
              <div className="p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between"><span>Distance estimée</span><span>{quote.distance_km} km</span></div>
                <div className="flex justify-between"><span>Frais de base</span><span>{formatCurrency(quote.base_fee)}</span></div>
                <div className="flex justify-between"><span>Poids ({formData.packageWeight} kg)</span><span>{formatCurrency(quote.weight_fee)}</span></div>
                <div className="flex justify-between"><span>Distance</span><span>{formatCurrency(quote.distance_fee)}</span></div>
                {quote.category_multiplier > 1 && (
                  <div className="flex justify-between"><span>Majoration catégorie</span><span>×{quote.category_multiplier}</span></div>
                )}
              </div>
            )}
            <p className="text-2xl font-bold text-emerald-600">Total : {formatCurrency(totalPrice)}</p>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Mode de paiement</h3>
              {paymentMethods.length === 0 ? (
                <p className="text-sm text-amber-600">Aucun mode de paiement configuré.</p>
              ) : (
                <div className="grid gap-3">
                  {paymentMethods.map((method) => {
                    const selected = formData.paymentMethodId === method.id;
                    const cash = isCashMethod(method.name);
                    return (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => setFormData((p) => ({
                          ...p,
                          paymentMethodId: method.id,
                          payNow: isMobileMoneyMethod(method.name),
                        }))}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          selected ? 'bg-teal-50 border-teal-400' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center ${
                            cash ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'
                          }`}>
                            {cash ? <Banknote className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">{method.name}</p>
                            {method.description && (
                              <p className="text-sm text-gray-500 mt-0.5">{method.description}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              {cash
                                ? 'Aucun numéro Mobile Money requis — paiement au livreur'
                                : 'Vous renseignerez opérateur et numéro à l’étape suivante'}
                            </p>
                          </div>
                          {selected && <Check className="w-5 h-5 text-teal-600 shrink-0" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <DashboardLayout userType="client" title="Nouvelle commande">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="flex items-center gap-2 mb-8 overflow-x-auto">
        {steps.map((s, i) => (
          <div key={s.key} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${i <= currentStepIndex ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
              {i < currentStepIndex ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-sm whitespace-nowrap ${i === currentStepIndex ? 'font-medium text-teal-700' : 'text-gray-500'}`}>{s.label}</span>
            {i < steps.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300" />}
          </div>
        ))}
      </div>

      <Card className="mb-6">{renderStepContent()}</Card>

      <div className="flex justify-between">
        <Button variant="outline" leftIcon={<ChevronLeft className="w-4 h-4" />} onClick={() => currentStepIndex > 0 && setStep(steps[currentStepIndex - 1].key)} disabled={currentStepIndex === 0}>
          Retour
        </Button>
        {currentStepIndex < steps.length - 1 ? (
          <Button rightIcon={<ChevronRight className="w-4 h-4" />} onClick={handleNext} disabled={!canProceed()}>Suivant</Button>
        ) : (
          <Button onClick={handleSubmit} isLoading={submitting} disabled={!canProceed()}>Confirmer la commande</Button>
        )}
      </div>
    </DashboardLayout>
  );
}
